/**
 * Emergent Auth Provider
 * Handles authentication via Emergent's hosted gateway (auth.emergentagent.com)
 * 
 * This file isolates all Emergent-specific auth logic.
 * App code should NEVER import this directly - use src/auth/index.js instead.
 */

const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com';

/**
 * Initiate Google login via Emergent gateway
 * @param {string} redirectPath - Path to redirect after auth (default: /dashboard)
 */
export function loginWithGoogle(redirectPath = '/dashboard') {
  const redirectUrl = window.location.origin + redirectPath;
  window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
}

/**
 * Handle auth callback from Emergent
 * Emergent handles this automatically via cookies, so this is a no-op
 * @returns {Promise<{success: boolean, user: object|null, error: string|null}>}
 */
export async function handleAuthCallback() {
  // Emergent auth callback is handled server-side via cookies
  // The user is already authenticated when they reach the callback
  return { success: true, user: null, error: null };
}

/**
 * Get current user from Emergent session
 * @param {string} apiUrl - Backend API URL
 * @returns {Promise<object|null>}
 */
export async function getCurrentUser(apiUrl) {
  try {
    const response = await fetch(`${apiUrl}/auth/me`, {
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
      }
    });
    
    // For non-OK responses (401, 403, etc), return null immediately
    // Do NOT try to read the body - this prevents "body stream already read" errors
    if (!response.ok) {
      return null;
    }
    
    // Only read body for successful responses
    try {
      const data = await response.json();
      return data;
    } catch (parseError) {
      console.error('[EmergentAuth] Failed to parse /auth/me response:', parseError);
      return null;
    }
  } catch (error) {
    // Network errors, CORS issues, etc - silently return null
    console.error('[EmergentAuth] getCurrentUser error:', error);
    return null;
  }
}

/**
 * Logout from Emergent session
 * @param {string} apiUrl - Backend API URL
 */
export async function logout(apiUrl) {
  try {
    await fetch(`${apiUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    localStorage.removeItem('auth_token');
  } catch (error) {
    console.error('[EmergentAuth] logout error:', error);
  }
}

/**
 * Check if user is authenticated via Emergent
 * @param {string} apiUrl - Backend API URL
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated(apiUrl) {
  const user = await getCurrentUser(apiUrl);
  return !!user;
}
